import type { CaptureResult } from 'posthog-js';

/**
 * Redact PII from PostHog autocapture-family events.
 *
 * Unconditionally strips `$el_text` and `attr__aria-label` from every
 * `$autocapture`, `$rageclick`, and `$dead_click` event — at the top-level
 * properties, every entry in the `$elements` chain, and the serialized
 * `$elements_chain` string.
 *
 * This preserves click taxonomy (tag, page, position, chain structure) while
 * removing user-visible text that may contain PII. Session recording handles
 * input content separately (see `maskInputOptions` in posthog.tsx).
 *
 * PostHog serializes the elements chain as a string. Text appears as
 * `text="..."` and aria-labels as `attr__aria-label="..."` key-value pairs.
 * The regex uses a negative lookbehind to avoid matching attribute names
 * that happen to end in "text" (e.g., `attr__data-context`).
 */

const REDACTED_EVENTS = new Set(['$autocapture', '$rageclick', '$dead_click']);

const CHAIN_PII_PATTERN = /(?<![\w-])(?:text|attr__aria-label)="(?:[^"\\]|\\.)*"/g;

export function redactAutocaptureEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || !REDACTED_EVENTS.has(event.event)) return event;
  if (!event.properties) return event;

  const { $el_text: _topText, 'attr__aria-label': _topLabel, ...rest } = event.properties;

  const elements = rest.$elements as Record<string, unknown>[] | undefined;
  if (Array.isArray(elements)) {
    rest.$elements = elements.map(
      ({ $el_text: _elText, 'attr__aria-label': _elLabel, ...el }) => el
    );
  }

  if (typeof rest.$elements_chain === 'string') {
    rest.$elements_chain = rest.$elements_chain.replace(CHAIN_PII_PATTERN, '');
  }

  return { ...event, properties: rest };
}
